import axiosInstance from '../axiosInstance';
import { initializeDB } from '../config/db';
import { UNIFIED_FORM_STORE } from '../config/constants';

function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * Generic UnifiedForm service for offline-first create + sync
 */
const unifiedFormService = {
    /**
     * Save or update a local draft/pending unified form
     * @param {string} type unified form type (e.g., 'post_work_area_turnover')
     * @param {object} payload arbitrary payload to persist (incl. data/meta)
     * @param {string} [uuid]
     * @returns {Promise<{uuid: string}>}
     */
    async saveLocal(type, payload, uuid = null) {
        const db = await initializeDB();
        const tx = db.transaction(UNIFIED_FORM_STORE, 'readwrite');
        const store = tx.objectStore(UNIFIED_FORM_STORE);

        const now = new Date().toISOString();
        const record = {
            uuid: uuid || payload.uuid || generateUUID(),
            type,
            status: payload.status || 'draft',
            payload,
            updatedAt: now,
            createdAt: now,
        };

        const existing = await store.get(record.uuid);
        if (existing) {
            record.createdAt = existing.createdAt || now;
            // Keep submittedAt if it was already submitted
            if (existing.status === 'submitted') {
                record.status = 'submitted';
                record.submittedAt = existing.submittedAt || now;
            }
        }
        await store.put(record);
        return { uuid: record.uuid };
    },

    /**
     * Submit to API. If offline or API fails, mark as pending for sync.
     * @param {string} type
     * @param {object} payload
     * @param {string} [uuid]
     */
    async submit(type, payload, uuid = null) {
        // Save as draft first (not submitted yet)
        const { uuid: ensuredUuid } = await this.saveLocal(type, { ...payload, status: 'draft' }, uuid);
        const db = await initializeDB();
        // Read snapshot in short tx (avoid holding tx across network)
        let local;
        {
            const txRead = db.transaction(UNIFIED_FORM_STORE, 'readonly');
            const storeRead = txRead.objectStore(UNIFIED_FORM_STORE);
            local = await storeRead.get(ensuredUuid);
            await txRead.done?.catch(() => {});
        }

        // Attempt API call if online
        if (navigator.onLine) {
            try {
                const res = await axiosInstance.post(`/forms/${encodeURIComponent(type)}`, {
                    ...payload,
                    uuid: ensuredUuid,
                    status: 'submitted',
                });
                const httpOk = !!res && typeof res.status === 'number' && res.status >= 200 && res.status < 300;
                const apiOk = !!(res?.data?.success);
                if (httpOk && apiOk) {
                    // API accepted - mark as submitted
                    try {
                        const txWrite = db.transaction(UNIFIED_FORM_STORE, 'readwrite');
                        const storeWrite = txWrite.objectStore(UNIFIED_FORM_STORE);
                        local = local || { uuid: ensuredUuid, type, payload };
                        local.status = 'submitted';
                        local.serverId = res?.data?.id ?? local.serverId;
                        local.submittedAt = new Date().toISOString();
                        await storeWrite.put(local);
                        await txWrite.done?.catch(() => {});
                    } catch (dbErr) {
                        console.error('Failed to update IndexedDB after successful submit:', dbErr);
                        // Continue anyway since server accepted the form
                    }

                    // Always return success if server responded successfully
                    return { success: true, uuid: ensuredUuid, id: res?.data?.id };
                } else {
                    // API returned error - keep as draft and return error
                    return { success: false, message: res?.data?.message || 'Server rejected the form', uuid: ensuredUuid };
                }
            } catch (e) {
                console.error('API error:', e);
                // Check if it's a server error (got HTTP response) or network error (no response)
                if (e?.response) {
                    // Server responded with error status (4xx, 5xx) - don't queue, return error
                    const errorMessage = e?.response?.data?.message || e?.message || 'Server error occurred';
                    return { success: false, message: errorMessage, uuid: ensuredUuid };
                }
                // Network error (no response) - will queue below
            }
        }

        // Queue as pending (offline or network error)
        {
            const txQueue = db.transaction(UNIFIED_FORM_STORE, 'readwrite');
            const storeQueue = txQueue.objectStore(UNIFIED_FORM_STORE);
            local = local || { uuid: ensuredUuid, type, payload };
            local.status = 'pending';
            local.updatedAt = new Date().toISOString();
            await storeQueue.put(local);
            await txQueue.done?.catch(() => {});
        }
        return { success: false, queued: true, uuid: ensuredUuid };
    },

    /**
     * Sync all pending unified forms
     */
    async syncPending() {
        if (!navigator.onLine) return;
        const db = await initializeDB();
        if (!db.objectStoreNames.contains(UNIFIED_FORM_STORE)) return;
        const tx = db.transaction(UNIFIED_FORM_STORE, 'readwrite');
        const store = tx.objectStore(UNIFIED_FORM_STORE);
        const index = store.index('status');
        const pending = await index.getAll('pending');

        for (const record of pending) {
            try {
                const res = await axiosInstance.post(`/forms/${encodeURIComponent(record.type)}`, {
                    ...record.payload,
                    uuid: record.uuid,
                    status: 'submitted',
                });
                if (res?.data?.success) {
                    record.status = 'submitted';
                    record.serverId = res.data.id;
                    record.submittedAt = new Date().toISOString();
                    await store.put(record);
                }
            } catch (e) {
                // keep pending
            }
        }
    }
};

export default unifiedFormService;

// Extra helpers for list views
export async function getAllByType(type) {
    const db = await initializeDB();
    if (!db.objectStoreNames.contains(UNIFIED_FORM_STORE)) return [];
    const tx = db.transaction(UNIFIED_FORM_STORE, 'readonly');
    const store = tx.objectStore(UNIFIED_FORM_STORE);
    const all = await store.getAll();
    return (all || []).filter(r => r.type === type).sort((a, b) => {
        return (b.updatedAt || '').localeCompare(a.updatedAt || '');
    });
}

export async function deleteLocal(uuid) {
    const db = await initializeDB();
    if (!db.objectStoreNames.contains(UNIFIED_FORM_STORE)) return;
    const tx = db.transaction(UNIFIED_FORM_STORE, 'readwrite');
    const store = tx.objectStore(UNIFIED_FORM_STORE);
    await store.delete(uuid);
}

export async function getByUUID(uuid) {
    const db = await initializeDB();
    if (!db.objectStoreNames.contains(UNIFIED_FORM_STORE)) return null;
    const tx = db.transaction(UNIFIED_FORM_STORE, 'readonly');
    const store = tx.objectStore(UNIFIED_FORM_STORE);
    return store.get(uuid);
}
